import React from 'react';
import {Button} from '@features/ui/components/Button';
import {cn} from '@features/ui/utils';
import type {PlanFeature} from './planFeatureList';
import {BananaSpinner} from '@features/ui/components/BananaSpinner';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@features/ui/components/tooltip';
import InfoIcon from '@/icons/info.svg';
import PlanFreeIcon from '@/icons/plan-free.svg';
import PlanPlusIcon from '@/icons/plan-plus.svg';
import PlanProIcon from '@/icons/plan-pro.svg';

interface AnnualToggleProps {
  isAnnual: boolean;
  onToggle: (isAnnual: boolean) => void;
  monthlyPrice: string;
  yearlyPrice: string;
  yearlyMonthlyEquivalent: string;
  discountPercent: number;
  perSeat?: boolean;
  disabled?: boolean;
}

type PlanNameIcon = 'free' | 'plus' | 'pro';

interface PricingCardProps {
  title: string;
  planIcon?: PlanNameIcon;
  price: string;
  period?: string;
  originalPrice?: string;
  features: PlanFeature[];
  buttonText: string;
  buttonAction?: () => void;
  isCurrentPlan?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  buttonVariant?: 'primary' | 'secondary';
  annualToggle?: AnnualToggleProps;
}

const planIconMap = {
  free: PlanFreeIcon,
  plus: PlanPlusIcon,
  pro: PlanProIcon,
};

export default function PricingCard({
  title,
  planIcon,
  price,
  period,
  originalPrice,
  features,
  buttonText,
  buttonAction = () => {},
  isCurrentPlan = false,
  disabled = false,
  isLoading = false,
  buttonVariant = 'primary',
  annualToggle,
}: PricingCardProps) {
  const PlanIcon = planIcon ? planIconMap[planIcon] : null;

  return (
    <div className='flex w-[304px] flex-col rounded-2xl bg-muted p-6'>
      {/* Header row: Plan icon + Annual toggle */}
      <div className='mb-3 flex items-center justify-between'>
        {PlanIcon ? (
          <PlanIcon className='h-6' />
        ) : (
          <h3 className='text-sm font-semibold text-foreground'>{title}</h3>
        )}
        {annualToggle && (
          <button
            onClick={() => annualToggle.onToggle(!annualToggle.isAnnual)}
            className={cn(
              'flex items-center gap-1.5',
              annualToggle.disabled && 'cursor-not-allowed opacity-50',
            )}
            disabled={annualToggle.disabled}
          >
            <span className='text-sm font-medium text-black/40'>
              Yearly -{annualToggle.discountPercent}%
            </span>
            <div
              className={cn(
                'relative h-5 w-8 rounded-full transition-colors',
                annualToggle.isAnnual ? 'bg-primary' : 'bg-gray-300',
              )}
            >
              <div
                className={cn(
                  'absolute top-[3px] size-[14px] rounded-full bg-white shadow-sm transition-transform',
                  annualToggle.isAnnual
                    ? 'translate-x-[15px]'
                    : 'translate-x-[3px]',
                )}
              />
            </div>
          </button>
        )}
      </div>

      {/* Price */}
      <div className='mb-4'>
        <div className='flex items-baseline gap-1'>
          {originalPrice && (
            <span className='text-lg font-semibold text-black/40 line-through'>
              {originalPrice}
            </span>
          )}
          <span className='text-xl font-semibold text-foreground'>
            {annualToggle
              ? annualToggle.isAnnual
                ? `${annualToggle.yearlyMonthlyEquivalent}/month${annualToggle.perSeat ? '/seat' : ''}`
                : `${annualToggle.monthlyPrice}${period ?? ''}`
              : `${price}${period ?? ''}`}
          </span>
          {annualToggle?.isAnnual && (
            <span className='text-xl font-semibold text-gray-400/70 line-through decoration-gray-400/70'>
              {annualToggle.monthlyPrice}
            </span>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className='-mx-6 mb-5 mt-1 h-px bg-black/10' />

      <Button
        variant={isCurrentPlan ? 'disabled' : buttonVariant}
        size='md'
        onClick={buttonAction}
        disabled={disabled || isCurrentPlan || isLoading}
        className='mb-5 w-full items-center justify-center'
      >
        {isLoading ? <BananaSpinner /> : buttonText}
      </Button>

      <div className='space-y-2.5'>
        {features.map((feature, index) => (
          <div
            key={index}
            className={cn(
              'flex items-center gap-1.5',
              feature.available ? 'opacity-100' : 'opacity-30',
            )}
          >
            <feature.icon
              className={cn(
                'h-5 w-5 shrink-0 text-gray-400',
                !feature.available && 'text-black/70',
              )}
            />
            <span className='flex flex-1 items-center gap-1'>
              {!feature.available && (
                <span className='flex h-5 items-center rounded-md border-1 border-black/70 px-1 text-xs font-medium text-black/70'>
                  Soon
                </span>
              )}
              <span className='text-sm font-medium text-black/70'>
                {feature.text}
              </span>
            </span>
            {feature.tooltip && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type='button' className='flex items-center'>
                    <InfoIcon className='h-5 w-5 text-black/15' />
                  </button>
                </TooltipTrigger>
                <TooltipContent className='whitespace-pre-line text-center'>
                  {feature.tooltip}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
